import React, { FC, useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../../_metronic/layout/core'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { ToastContainer } from 'react-toastify'
import { AddAdvertisementHeader } from './AddAdvertisementHeader'
import { fetchGetData, toastsuccessmsg, toasterrormsg, fetchData, fetchFormData, getPlainTextLength } from '../../../../reusable'
import {
  placementCropDim,
  USER_LEVELS,
  type PlacementOption,
} from './AddAdvertisement.constants'
import AddAdvertisementPlacementTab from './AddAdvertisementPlacementTab'
import AddAdvertisementTargetingTab from './AddAdvertisementTargetingTab'
import AddAdvertisementBannerTab from './AddAdvertisementBannerTab'
import AddAdvertisementDetailTab from './AddAdvertisementDetailTab'
import { type PromotionSelection } from './AddAdvertisementPromotionSelect'
import AddAdvertisementBudgetTab from './AddAdvertisementBudgetTab'

interface AdsSettingApiResponse {
  status: number
  message: string
  data: PlacementOption[]
  success: boolean
}

const AddAdvertisement: FC = () => {
  const t = Tr();
  const lang = useLang()
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = location.state as { adsData?: any; adsId?: string | number; tab?: number; isNew?: boolean; paymentFailed?: boolean } | null
  const [editAdvertisementId, setEditAdvertisementId] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const fetchAdDataOnMount = useRef(false)

  // Persist adsId + active tab into browser history state (invisible to user, survives refresh)
  const persistState = (adsId: string, tab: number) => {
    navigate(location.pathname, { replace: true, state: { adsId, tab } })
  }

  // Change tab and persist to history so refresh restores the same tab
  const changeTab = (tab: number) => {
    setActiveTab(tab)
    if (editAdvertisementId) {
      persistState(editAdvertisementId, tab)
    }
  }

  useEffect(() => {
    if (locationState?.paymentFailed) {
      toasterrormsg(t('Payment failed. Please try again.'))
      navigate(location.pathname, { replace: true, state: { ...locationState, paymentFailed: undefined } })
    }
  }, [])

  // Set edit id from location state (edit from list, resume from refresh, or new)
  useEffect(() => {
    const stateAdsId = locationState?.adsId ? String(locationState.adsId) : ''
    if (locationState?.isNew) {
      // Deliberately creating a new ad — clear any leftover state
      sessionStorage.removeItem('editAdvertisementId')
      sessionStorage.removeItem('adActiveTab')
      // editAdvertisementId stays '', no fetchAdData needed
    } else if (stateAdsId) {
      // Edit from list OR page refresh mid-creation (both carry adsId in location state)
      fetchAdDataOnMount.current = true
      setEditAdvertisementId(stateAdsId)
      if (locationState?.tab !== undefined) {
        setActiveTab(locationState.tab)
      }

      // Pre-fill basic fields from list-row adsData if present (edit from list)
      const row = locationState?.adsData
      if (row) {
        if (row.en_name || row.ru_name || row.uz_name) {
          setDetailForm(prev => ({
            ...prev,
            title: row.en_name || '',
            titleRu: row.ru_name || '',
            titleUz: row.uz_name || '',
          }))
        }
        if (row.duration && row.durationType) {
          setSelectedWeeks(row.durationType === 'MONTH' ? 4 : row.durationType === 'DAY' ? 0 : Number(row.duration) || 1)
        }
        if (row.adssettingId) {
          setSelectedAdssettingId(String(row.adssettingId))
        }
      }
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const fetchPlacementOptions = async () => {
      setPlacementOptionsLoading(true)
      setPlacementOptionsError('')

      try {
        const response = await fetchGetData(`public/getadssettinglist${categoryId ? `?categoryId=${categoryId}` : ''}`)
        const typedResponse = response as AdsSettingApiResponse
        const apiPlacements = Array.isArray(typedResponse?.data) ? typedResponse.data : []

        if (!isMounted) return

        setPlacementOptions(apiPlacements)

        if (apiPlacements.length === 0) {
          setPlacementOptionsError(typedResponse?.message || 'No placement data found.')
        }
      } catch (error) {
        if (!isMounted) return

        console.error('Failed to fetch advertisement placements', error)
        setPlacementOptions([])
        setPlacementOptionsError('Failed to load placement list.')
      } finally {
        if (isMounted) {
          setPlacementOptionsLoading(false)
        }
      }
    }

    fetchPlacementOptions()

    return () => {
      isMounted = false
    }
  }, [])

  var vendorId = ''
  const vendorData = sessionStorage.getItem("vendor")
  if (vendorData) {
    var vendor = JSON.parse(vendorData)
    vendorId = vendor.vendorId
  }
  const categoryId = sessionStorage.getItem('categoryId') || ''

  // Placement tab state (legacy - unused after new form)
  const [placementForm, setPlacementForm] = useState({
    image: null as File | null,
    title: '',
    subtitle: ''
  })
  const [placementErrors, setPlacementErrors] = useState({
    image: '',
    title: '',
    subtitle: ''
  })

  // New Placement form state
  const [placementFormData, setPlacementFormData] = useState({
    name: '',
    placement: '',
    days: ''
  })
  const [selectedAdssettingId, setSelectedAdssettingId] = useState<string | null>(null)
  const [placementFormErrors, setPlacementFormErrors] = useState({
    name: '',
    placement: '',
    days: ''
  })
  const [placementOptions, setPlacementOptions] = useState<PlacementOption[]>([])
  const [placementOptionsLoading, setPlacementOptionsLoading] = useState(false)
  const [placementOptionsError, setPlacementOptionsError] = useState('')

  // Targeting tab state
  const [targetingForm, setTargetingForm] = useState({
    gender: '',
    // Multi-select — empty means "All Regions"
    region: [] as string[],
    // Age is optional — empty min/max means "All Ages"
    ageMin: '',
    ageMax: '',
    // Multi-select — empty means "All Levels"
    userLevel: [] as string[],
    interest: [] as string[]
  })
  // Banner tab state
  const [bannerForm, setBannerForm] = useState({
    image: null as File | null,
    title: '',
    subtitle: '',
    titleRu: '',
    subtitleRu: '',
    titleUz: '',
    subtitleUz: ''
  })
  const [bannerErrors, setBannerErrors] = useState({
    image: '',
    title: '',
    subtitle: '',
    titleRu: '',
    titleUz: ''
  })
  const [bannerSourceImage, setBannerSourceImage] = useState('')
  const [existingBannerUrl, setExistingBannerUrl] = useState('')
  const [adsbannerId, setAdsbannerId] = useState<string | null>(null)
  const [bannerCropZoom, setBannerCropZoom] = useState(1)
  const [bannerCropX, setBannerCropX] = useState(0)
  const [bannerCropY, setBannerCropY] = useState(0)
  const [bannerCropApplied, setBannerCropApplied] = useState(false)
  const [bannerIsNewImage, setBannerIsNewImage] = useState(false)

  // Budget tab state
  const [selectedWeeks, setSelectedWeeks] = useState(1)
  const [customDuration, setCustomDuration] = useState<{ value: number; type: 'DAY' | 'WEEK' | 'MONTH' }>({ value: 1, type: 'WEEK' })
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [priceApiData, setPriceApiData] = useState<{ perDayPrice: number; totalPrice: number } | null>(null)
  const [adData, setAdData] = useState<any>(null)
  const [imageBaseUrl, setImageBaseUrl] = useState('')

  // Detail tab state
  const [detailForm, setDetailForm] = useState({
    title: '',
    titleRu: '',
    titleUz: '',
    mainDescription: '',
    descriptionRu: '',
    descriptionUz: '',
    galleryImages: [] as File[],
    promoCode: ''
  })
  const [detailErrors, setDetailErrors] = useState({
    title: '',
    titleRu: '',
    titleUz: '',
    mainDescription: '',
    descriptionRu: '',
    descriptionUz: '',
    galleryImages: '',
    promoCode: ''
  })

  // Existing media images fetched from API (Detail tab)
  const [existingMediaImages, setExistingMediaImages] = useState<{ adsmediaId: number; adsId: number; image: string }[]>([])

  const fetchMediaImages = async (adsId: string) => {
    try {
      const res: any = await fetchData('public/getadsmedia', {
        vendorId: String(vendorId),
        adsId: Number(adsId),
      })
      if (String(res?.status) === '200') {
        setExistingMediaImages(Array.isArray(res.data) ? res.data : [])
      }
    } catch (err) {
      console.error('Failed to fetch media images', err)
    }
  }

  // Promotion selection state (Detail tab)
  const [promotionSelection, setPromotionSelection] = useState<PromotionSelection>({
    parentId: '',
    childIds: [],
  })
  // Raw actionTypeId from API (used to restore promotion selection on edit)
  const [adActionTypeId, setAdActionTypeId] = useState<string[]>([])

  const detailEditorRef = useRef(null)
  const bannerDragRef = useRef({ dragging: false, startX: 0, startY: 0, startCropX: 0, startCropY: 0 })
  const bannerContainerRef = useRef<HTMLDivElement>(null)

  const fetchAdData = async (adsId: string) => {
    try {
      const res: any = await fetchData('public/getad', {
        vendorId: Number(vendorId),
        adsId: Number(adsId),
      })
      if (String(res?.status) === '200') {
        const data = res.data
        const baseUrl = res.url || ''
        setAdData(data)
        setImageBaseUrl(baseUrl)

        // Duration / weeks
        if (data.duration && data.durationType) {
          const dur = Number(data.duration) || 1
          if (data.durationType === 'MONTH') {
            if (dur === 1) {
              setSelectedWeeks(4) // fixed "1 Month" option
            } else {
              setSelectedWeeks(0) // custom
              setCustomDuration({ value: dur, type: 'MONTH' })
            }
          } else if (data.durationType === 'DAY') {
            setSelectedWeeks(0) // custom
            setCustomDuration({ value: dur, type: 'DAY' })
          } else {
            if (dur === 1 || dur === 2) {
              setSelectedWeeks(dur) // fixed 1W / 2W option
            } else {
              setSelectedWeeks(0) // custom
              setCustomDuration({ value: dur, type: 'WEEK' })
            }
          }
        }

        // Payment
        if (data.paymentStatus === 'COMPLETED' || data.paymentStatus === 2) {
          setPaymentSuccess(true)
        }
        if (data.perDayPrice || data.price) {
          setPriceApiData({ perDayPrice: data.perDayPrice, totalPrice: data.price })
        }

        // Placement
        if (data.adssettingId) {
          setSelectedAdssettingId(String(data.adssettingId))
        }

        // Detail form
        setDetailForm(prev => ({
          ...prev,
          title: data.en_name || '',
          titleRu: data.ru_name || '',
          titleUz: data.uz_name || '',
          mainDescription: data.en_description || '',
          descriptionRu: data.ru_description || '',
          descriptionUz: data.uz_description || '',
          promoCode: data.promocode || '',
        }))

        // Promotion items (child IDs — parent will be resolved inside the select component)
        if (Array.isArray(data.actionTypeId) && data.actionTypeId.length) {
          setAdActionTypeId(data.actionTypeId.map(String))
        }

        // Banner form
        if (data.banner) {
          setBannerForm(prev => ({
            ...prev,
            title: data.banner.en_title || '',
            titleRu: data.banner.ru_title || '',
            titleUz: data.banner.uz_title || '',
            subtitle: data.banner.en_subtitle || '',
            subtitleRu: data.banner.ru_subtitle || '',
            subtitleUz: data.banner.uz_subtitle || '',
          }))
          if (data.banner.adsbannerId) {
            setAdsbannerId(String(data.banner.adsbannerId))
          }
          const existingImg = data.banner.bannerImage || data.banner.image_312x152 || ''
          if (existingImg) {
            const fullImgUrl = `${baseUrl}${existingImg}`
            setExistingBannerUrl(fullImgUrl)
            // Pre-load as data URL so canvas crop works without CORS taint
            try {
              const dataUrl = await fetchImageAsDataUrl(fullImgUrl)
              setBannerSourceImage(dataUrl)
            } catch {
              setBannerSourceImage(fullImgUrl)
            }
            setBannerIsNewImage(false)
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch ad data', err)
    }
  }

  useEffect(() => {
    if (editAdvertisementId && fetchAdDataOnMount.current) {
      fetchAdDataOnMount.current = false
      fetchAdData(editAdvertisementId)
    }
  }, [editAdvertisementId])

  // Pre-populate placement name once placement options are loaded and adData is available
  useEffect(() => {
    if (!adData?.adssettingId || !placementOptions.length) return
    const option = placementOptions.find(o => o.adssettingId === adData.adssettingId)
    if (option) {
      setPlacementFormData(prev => ({ ...prev, placement: option.placementName }))
    }
  }, [adData, placementOptions])

  // Pre-populate targeting form from adData targets using fixed adstargetmasterId keys:
  // 1=age, 2=gender, 3=userLevel, 4=region, 5=interest
  useEffect(() => {
    if (!adData?.targets?.length) return
    const newForm = { gender: '', region: [] as string[], ageMin: '', ageMax: '', userLevel: [] as string[], interest: [] as string[] }
    for (const target of adData.targets) {
      const { adstargetmasterId, value, isActive } = target
      if (!value?.length) continue

      if (adstargetmasterId === 1) {
        // Age: value = [from, to]; inactive or [0, 0] means "All Ages"
        const [from, to] = value
        const noAgeFilter = !isActive || (Number(from) === 0 && Number(to) === 0)
        newForm.ageMin = noAgeFilter ? '' : String(from)
        newForm.ageMax = noAgeFilter ? '' : String(to)
      } else if (adstargetmasterId === 2) {
        // Gender: value = ["MALE"] or ["MALE","FEMALE"]
        newForm.gender = !isActive || value.length > 1
          ? ''
          : String(value[0]).toLowerCase()
      } else if (adstargetmasterId === 3) {
        // User Level: value = ["Basic"] or multiple; all four selected means "All Levels"
        const levels = !isActive ? [] : value.map((v: any) => String(v).toLowerCase())
        newForm.userLevel = levels.length === USER_LEVELS.length ? [] : levels
      } else if (adstargetmasterId === 4) {
        // Region: value = [regionId, ...]; inactive or the [0] placeholder means "All Regions"
        newForm.region = !isActive
          ? []
          : value.map((v: any) => String(v)).filter((v: string) => v !== '0')
      } else if (adstargetmasterId === 5) {
        // Interest: value = [interestId, ...]
        newForm.interest = !isActive
          ? []
          : value.map((v: any) => String(v)).filter((v: string) => v !== '0')
      }
    }
    setTargetingForm(newForm)
  }, [adData])

  // Placement handlers (legacy)
  const handlePlacementInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'title' && value.length > 40) {
      setPlacementErrors(prev => ({ ...prev, title: t('Title must be maximum 40 characters') }))
      return
    }

    if (name === 'subtitle' && value.length > 60) {
      setPlacementErrors(prev => ({ ...prev, subtitle: t('Subtitle must be maximum 60 characters') }))
      return
    }

    setPlacementForm(prev => ({ ...prev, [name]: value }))
    setPlacementErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handlePlacementFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      const maxSize = 1 * 1024 * 1024 // 1MB

      if (!allowedTypes.includes(file.type)) {
        setPlacementErrors(prev => ({ ...prev, image: t('Only JPG, PNG, and WEBP files are allowed') }))
        return
      }

      if (file.size > maxSize) {
        setPlacementErrors(prev => ({ ...prev, image: t('File size must be less than 1MB') }))
        return
      }

      setPlacementForm(prev => ({ ...prev, image: file }))
      setPlacementErrors(prev => ({ ...prev, image: '' }))
    }
  }

  // Detail handlers
  const handleDetailInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    // Skip mainDescription - handled by JoditEditor
    if (name === 'mainDescription') return;

    if ((name === 'title' || name === 'titleRu' || name === 'titleUz') && value.length > 100) {
      setDetailErrors(prev => ({ ...prev, [name]: t('Title must be maximum 100 characters') }))
      return
    }

    if (name === 'promoCode' && value.length > 20) {
      setDetailErrors(prev => ({ ...prev, promoCode: t('Promo code must be maximum 20 characters') }))
      return
    }

    setDetailForm(prev => ({ ...prev, [name]: value }))
    setDetailErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleDetailEditorBlur =
    (field: 'mainDescription' | 'descriptionRu' | 'descriptionUz') => (newContent: string) => {
      // always keep the content so the counter reflects what's in the editor
      setDetailForm(prev => ({ ...prev, [field]: newContent }))
      setDetailErrors(prev => ({
        ...prev,
        [field]: getPlainTextLength(newContent) > 500 ? t('Description must be maximum 500 characters') : '',
      }))
    }

  const handleDetailEditorChange =
    (field: 'mainDescription' | 'descriptionRu' | 'descriptionUz') => (newContent: string) => {
      setDetailForm(prev => ({ ...prev, [field]: newContent }))
    }

  const handleDetailFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
      const maxSize = 2 * 1024 * 1024
      const maxImages = 6

      const remaining = maxImages - existingMediaImages.length - detailForm.galleryImages.length
      const filesToAdd = files.slice(0, Math.max(remaining, 0))
      const hasExceeded = files.length > remaining || remaining <= 0

      for (const file of filesToAdd) {
        if (!allowedTypes.includes(file.type)) {
          setDetailErrors(prev => ({ ...prev, galleryImages: t('Only JPG, PNG, and WEBP files are allowed') }))
          return
        }

        if (file.size > maxSize) {
          setDetailErrors(prev => ({ ...prev, galleryImages: t('Each file size must be less than 2MB') }))
          return
        }
      }

      if (filesToAdd.length > 0) {
        setDetailForm(prev => ({ ...prev, galleryImages: [...prev.galleryImages, ...filesToAdd] }))
      }

      if (hasExceeded) {
        setDetailErrors(prev => ({ ...prev, galleryImages: t('Maximum 6 images allowed') }))
      } else {
        setDetailErrors(prev => ({ ...prev, galleryImages: '' }))
      }
    }
  }

  const removeDetailImage = (index: number) => {
    setDetailForm(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index)
    }))
  }

  const handleTargetingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setTargetingForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
    const maxSize = 1 * 1024 * 1024

    if (!allowedTypes.includes(file.type)) {
      setBannerErrors((prev) => ({ ...prev, image: t('Only JPG, PNG, and WEBP files are allowed') }))
      return
    }
    if (file.size > maxSize) {
      setBannerErrors((prev) => ({ ...prev, image: t('File size must be less than 1MB') }))
      return
    }

    const reader = new FileReader()
    reader.onload = () => setBannerSourceImage(String(reader.result || ''))
    reader.readAsDataURL(file)

    // Reset crop state — user must apply crop before saving
    setBannerForm((prev) => ({ ...prev, image: null }))
    setBannerCropZoom(1)
    setBannerCropX(0)
    setBannerCropY(0)
    setBannerCropApplied(false)
    setBannerIsNewImage(true)
    setBannerErrors((prev) => ({ ...prev, image: '' }))
  }

  // Fetch an image URL → base64 data URL using the same apitoken all API calls use
  const fetchImageAsDataUrl = async (url: string): Promise<string> => {
    const headers = new Headers()
    headers.append('apitoken', process.env.REACT_APP_API_TOKEN || '')
    const r = await fetch(url, { method: 'GET', headers })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    const blob = await r.blob()
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  const applyBannerCrop = async () => {
    if (!bannerSourceImage) return
    const cropDim = placementCropDim[placementFormData.placement] || { w: 312, h: 152 }
    const rendW = bannerContainerRef.current?.offsetWidth || cropDim.w

    // Convert remote URL → data URL (with auth header) so the canvas is never tainted
    let srcForCanvas = bannerSourceImage
    if (srcForCanvas.startsWith('http')) {
      try {
        srcForCanvas = await fetchImageAsDataUrl(srcForCanvas)
        setBannerSourceImage(srcForCanvas)
      } catch {
        toasterrormsg(t('Could not load image for cropping. Please re-upload the image.'))
        return
      }
    }

    const img = new Image()
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const PX = 2
        canvas.width = cropDim.w * PX
        canvas.height = cropDim.h * PX
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const s = canvas.width / rendW
        const rendH = canvas.height / s
        const rendImgH = rendW * (img.naturalHeight / img.naturalWidth)

        const drawX = (rendW / 2 * (1 - bannerCropZoom) + bannerCropX) * s
        const drawY = (rendH / 2 - rendImgH / 2 * bannerCropZoom + bannerCropY) * s
        const drawW = rendW * bannerCropZoom * s
        const drawH = rendImgH * bannerCropZoom * s

        ctx.drawImage(img, drawX, drawY, drawW, drawH)
        canvas.toBlob((blob) => {
          if (!blob) {
            toasterrormsg(t('Failed to apply crop. Please try again.'))
            return
          }
          const file = new File([blob], 'banner.webp', { type: 'image/webp' })
          setBannerForm((prev) => ({ ...prev, image: file }))
          setBannerCropApplied(true)
          setBannerErrors((prev) => ({ ...prev, image: '' }))
          window.dispatchEvent(new CustomEvent('showToast', { detail: { type: 'success', message: t('Crop applied successfully') } }))
        }, 'image/webp', 0.95)
      } catch {
        toasterrormsg(t('Failed to apply crop. Please try again.'))
      }
    }
    img.onerror = () => toasterrormsg(t('Failed to load image for cropping.'))
    img.src = srcForCanvas
  }

  const handleCropMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    bannerDragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startCropX: bannerCropX, startCropY: bannerCropY }
  }

  const handleCropMouseMove = (e: React.MouseEvent) => {
    if (!bannerDragRef.current.dragging) return
    setBannerCropX(bannerDragRef.current.startCropX + (e.clientX - bannerDragRef.current.startX))
    setBannerCropY(bannerDragRef.current.startCropY + (e.clientY - bannerDragRef.current.startY))
    setBannerCropApplied(false)
  }

  const handleCropMouseUp = () => { bannerDragRef.current.dragging = false }

  // Validation functions

  const validateBanner = () => {
    let isValid = true
    const newErrors = { image: '', title: '', subtitle: '', titleRu: '', titleUz: '' }

    if (!bannerForm.image && !existingBannerUrl) {
      newErrors.image = bannerSourceImage
        ? t('Please apply crop before continuing')
        : t('Banner image is required')
      isValid = false
    }

    if (!bannerForm.title.trim()) {
      newErrors.title = t('Title is required')
      isValid = false
    } else if (bannerForm.title.length > 40) {
      newErrors.title = t('Title must be maximum 40 characters')
      isValid = false
    }

    if (!bannerForm.titleRu.trim()) {
      newErrors.titleRu = t('Title (Russian) is required')
      isValid = false
    } else if (bannerForm.titleRu.length > 40) {
      newErrors.titleRu = t('Title must be maximum 40 characters')
      isValid = false
    }

    if (!bannerForm.titleUz.trim()) {
      newErrors.titleUz = t('Title (Uzbek) is required')
      isValid = false
    } else if (bannerForm.titleUz.length > 40) {
      newErrors.titleUz = t('Title must be maximum 40 characters')
      isValid = false
    }

    setBannerErrors(newErrors)
    return isValid
  }

  const handleBannerNext = async () => {
    if (!validateBanner()) return

    const adsId = editAdvertisementId
    if (!adsId) {
      toasterrormsg(t('Advertisement ID is missing. Please complete the placement step first.'))
      return
    }

    try {
      const formData = new FormData()
      formData.append('vendorId', String(vendorId))
      formData.append('adsId', adsId)
      if (adsbannerId) {
        formData.append('adsbannerId', adsbannerId)
      }
      formData.append('en_title', bannerForm.title)
      formData.append('ru_title', bannerForm.titleRu)
      formData.append('uz_title', bannerForm.titleUz)
      formData.append('en_subtitle', bannerForm.subtitle)
      formData.append('ru_subtitle', bannerForm.subtitleRu)
      formData.append('uz_subtitle', bannerForm.subtitleUz)
      if (bannerForm.image) {
        formData.append('bannerImage', bannerForm.image)
        formData.append('image_312x152', bannerForm.image)
        formData.append('image_312x79', bannerForm.image)
      }

      const res = await fetchFormData('public/addeditbanner', formData)
      if (String(res?.status) === '200') {
        const bannerId = res?.data?.adsbannerId || res?.data?.data?.adsbannerId
        if (bannerId) {
          setAdsbannerId(String(bannerId))
        }
        toastsuccessmsg(res?.data?.message || t('Banner saved successfully!'))
        await fetchAdData(adsId)
        persistState(adsId, 3)
        setActiveTab(3)
      } else {
        toasterrormsg(res?.message || t('Something went wrong'))
      }
    } catch (error) {
      toasterrormsg(t('Failed to save banner'))
      console.error('Save banner error:', error)
    }
  }

  const validateDetail = () => {
    let isValid = true
    const newErrors = { title: '', titleRu: '', titleUz: '', mainDescription: '', descriptionRu: '', descriptionUz: '', galleryImages: '', promoCode: '' }

    if (!detailForm.title.trim()) {
      newErrors.title = t('Title is required')
      isValid = false
    } else if (detailForm.title.length > 100) {
      newErrors.title = t('Title must be maximum 100 characters')
      isValid = false
    }

    if (!detailForm.titleRu.trim()) {
      newErrors.titleRu = t('Title (Russian) is required')
      isValid = false
    } else if (detailForm.titleRu.length > 100) {
      newErrors.titleRu = t('Title must be maximum 100 characters')
      isValid = false
    }

    if (!detailForm.titleUz.trim()) {
      newErrors.titleUz = t('Title (Uzbek) is required')
      isValid = false
    } else if (detailForm.titleUz.length > 100) {
      newErrors.titleUz = t('Title must be maximum 100 characters')
      isValid = false
    }

    const plainDesc = getPlainTextLength(detailForm.mainDescription)
    if (!plainDesc) {
      newErrors.mainDescription = t('Main description is required')
      isValid = false
    } else if (plainDesc > 500) {
      newErrors.mainDescription = t('Description must be maximum 500 characters')
      isValid = false
    }

    const plainDescRu = getPlainTextLength(detailForm.descriptionRu)
    if (!plainDescRu) {
      newErrors.descriptionRu = t('Description (Russian) is required')
      isValid = false
    } else if (plainDescRu > 500) {
      newErrors.descriptionRu = t('Description must be maximum 500 characters')
      isValid = false
    }

    const plainDescUz = getPlainTextLength(detailForm.descriptionUz)
    if (!plainDescUz) {
      newErrors.descriptionUz = t('Description (Uzbek) is required')
      isValid = false
    } else if (plainDescUz > 500) {
      newErrors.descriptionUz = t('Description must be maximum 500 characters')
      isValid = false
    }

    // Only require new images if there are no existing API images already uploaded
    if (detailForm.galleryImages.length === 0 && existingMediaImages.length === 0) {
      newErrors.galleryImages = t('At least one gallery image is required')
      isValid = false
    } else if (detailForm.galleryImages.length > 6) {
      newErrors.galleryImages = t('Maximum 6 images allowed')
      isValid = false
    }

    setDetailErrors(newErrors)
    return isValid
  }

  const handleDetailNext = async () => {
    if (!validateDetail()) return

    try {
      const payload = {
        vendorId: Number(vendorId),
        adsId: editAdvertisementId,
        en_name: detailForm.title,
        ru_name: detailForm.titleRu,
        uz_name: detailForm.titleUz,
        en_description: detailForm.mainDescription,
        ru_description: detailForm.descriptionRu,
        uz_description: detailForm.descriptionUz,
        promocode: detailForm.promoCode,
        adssettingId: String(selectedAdssettingId),
        status: 'DRAFT',
        duration: String(selectedWeeks),
      }

      const res = await fetchData('public/addeditadsbasic', payload)
      if (String(res?.status) === '200') {
        toastsuccessmsg(res?.data?.message || t('Details saved successfully!'))
        const adsId = res?.data?.adsId ? String(res.data.adsId) : editAdvertisementId
        if (res?.data?.adsId) {
          setEditAdvertisementId(adsId)
        }

        // Upload new gallery images only if the user selected any
        if (detailForm.galleryImages.length > 0) {
          for (const file of detailForm.galleryImages) {
            const formData = new FormData()
            formData.append('vendorId', String(vendorId))
            formData.append('adsId', adsId)
            formData.append('image', file)
            await fetchFormData('public/addeditadsmedia', formData)
          }
          setDetailForm(prev => ({ ...prev, galleryImages: [] }))
          await fetchMediaImages(adsId)
        }

        // Save promotion items (one call per selected child)
        const ACTION_TYPE_MAP: Record<string, string> = {
          '1': 'room',
          '3': 'restaurant',
          '4': 'space',
          '5': 'tour',
          '7': 'vehicle',
        }
        const actionType = ACTION_TYPE_MAP[categoryId] || null
        // Call the API when items are selected OR when previously saved items now need to be cleared
        if (actionType && (promotionSelection.childIds.length > 0 || adActionTypeId.length > 0)) {
          await fetchData('public/addeditadsaction', {
            vendorId: Number(vendorId),
            adsId,
            actionType,
            actionTypeId: promotionSelection.childIds.filter(
              id => id && id !== 'undefined' && id !== 'null'
            ),
          })
        }

        await fetchAdData(adsId)
        persistState(adsId, 4)
        setActiveTab(4)
      } else {
        toasterrormsg(res?.message || t('Something went wrong'))
      }
    } catch (error) {
      toasterrormsg(t('Failed to save details'))
      console.error('Save detail error:', error)
    }
  }

  const validateBudget = () => true

  const validatePlacementForm = () => {
    let isValid = true
    const newErrors: any = { ...placementFormErrors }

    if (!placementFormData.placement) {
      newErrors.placement = t('Placement is required')
      isValid = false
    }

    setPlacementFormErrors(newErrors)
    return isValid
  }

  const handleTargetingNext = async () => {
    const adsId = editAdvertisementId

    if (!adsId) {
      // toasterrormsg(t('Advertisement ID is missing. Please complete the placement step first.'))
      return
    }

    // Build target payload using fixed adstargetmasterId keys: 1=age, 2=gender, 3=userLevel, 4=region
    const target: Record<number, { value: any[]; isActive: boolean }> = {}

    // Age — key 1 (optional: empty min/max is sent as [0, 0] / inactive = all ages)
    const ageMin = String(targetingForm.ageMin ?? '').trim()
    const ageMax = String(targetingForm.ageMax ?? '').trim()
    target[1] = ageMin && ageMax
      ? { value: [Number(ageMin), Number(ageMax)], isActive: true }
      : { value: [0, 0], isActive: false }

    // Gender — key 2
    const genderValue = targetingForm.gender
      ? [targetingForm.gender.toUpperCase()]
      : ['MALE', 'FEMALE']
    target[2] = { value: genderValue, isActive: true }

    // User Level — key 3, multi-select; empty means all levels. Capitalized for the API ('basic' → 'Basic')
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    const selectedLevels = targetingForm.userLevel || []
    const userLevelValue = selectedLevels.length
      ? selectedLevels.map(capitalize)
      : USER_LEVELS.map(capitalize)
    target[3] = { value: userLevelValue, isActive: true }

    // Region — key 4
    // Multi-select; empty = all regions, sent as the [0] placeholder
    target[4] = {
      value: targetingForm.region?.length ? targetingForm.region.map((id) => Number(id)) : [0],
      isActive: true,
    }

    // Interest — key 5 (multi-select; empty = all interests)
    target[5] = {
      value: targetingForm.interest?.length ? targetingForm.interest.map((id) => Number(id)) : [0],
      isActive: true,
    }

    const payload: any = {
      vendorId,
      adsId: Number(adsId),
      target,
    }

    try {
      const res = await fetchData('public/addeditadstarget', payload)
      if (String(res?.status) === '200') {
        toastsuccessmsg(res?.message || t('Targeting saved successfully!'))
        await fetchAdData(adsId)
        persistState(adsId, 2)
        goToNextTab()
      } else {
        toasterrormsg(res?.message || t('Something went wrong'))
      }
    } catch (error) {
      toasterrormsg(t('Failed to save targeting'))
      console.error('Save targeting error:', error)
    }
  }

  const handlePlacementNext = async () => {
    if (validatePlacementForm() && selectedAdssettingId) {
      try {
        const isEditing = !!editAdvertisementId
        const payload: any = { adssettingId: selectedAdssettingId, vendorId }
        if (isEditing) {
          payload.adsId = editAdvertisementId
        }
        const res = await fetchData('public/addeditadsplacement', payload)

        if (String(res?.status) == "200") {
          toastsuccessmsg(res?.message || t('Placement saved successfully!'))
          const newAdsId = String(res?.data?.adsId)
          setEditAdvertisementId(newAdsId)
          await fetchAdData(newAdsId)
          persistState(newAdsId, 1)
          goToNextTab()
        } else {
          toasterrormsg(res?.message || t('Something went wrong'))
        }
      } catch (error) {
        toasterrormsg(t('Failed to save placement'))
        console.error('Save placement error:', error)
        return
      }
    } else {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { type: 'error', message: t('Please select a placement') } }))
    }
  }

  const getDurationParams = (weeks: number) => {
    if (weeks === 0) return { duration: String(customDuration.value), durationType: customDuration.type }
    if (weeks === 4) return { duration: '1', durationType: 'MONTH' }
    return { duration: String(weeks), durationType: 'WEEK' }
  }



  useEffect(() => {
    if (activeTab !== 3) return
    if (!editAdvertisementId) return
    fetchMediaImages(editAdvertisementId)
  }, [activeTab, editAdvertisementId])


  const getCalculatedPrice = () => {
    const pl = placementOptions.find((o) => o.placementName === placementFormData.placement)
    const pricePerDay = pl?.amountPerDay || 0
    const days = selectedWeeks === 0
      ? (customDuration.type === 'DAY' ? customDuration.value : customDuration.type === 'WEEK' ? customDuration.value * 7 : customDuration.value * 30)
      : selectedWeeks === 4 ? 30 : selectedWeeks * 7
    return String(pricePerDay * days)
  }

  const handleClickPay = async (): Promise<boolean> => {
    if (isPaymentProcessing || paymentSuccess) return false
    const adsId = editAdvertisementId
    if (!adsId) {
      toasterrormsg(t('Advertisement ID is missing.'))
      return false
    }
    setIsPaymentProcessing(true)
    const { duration, durationType } = getDurationParams(selectedWeeks)
    const price = getCalculatedPrice()
    const transactionNumber = String(adData?.transactionNumber || Date.now())
    try {
      const res: any = await fetchData('public/addeditadspayment', {
        isPurchase: 2,
        vendorId: Number(vendorId),
        adsId: Number(adsId),
        price,
        paymentMethod: 'click',
        transactionNumber,
        paymentStatus: 'PAID',
        duration,
        durationType,
      })
      if (String(res?.status) === '200') {
        // Store payment context so the return URL handler can call the status-update API
        localStorage.setItem('clickPaymentAdsId', adsId)
        localStorage.setItem('clickPaymentContext', JSON.stringify({
          vendorId: Number(vendorId),
          adsId: Number(adsId),
          price,
          transactionNumber,
          duration,
          durationType,
        }))
        return true
      } else {
        toasterrormsg(res?.message || t('Payment initiation failed. Please try again.'))
        return false
      }
    } catch (err) {
      toasterrormsg(t('Payment initiation failed. Please try again.'))
      console.error('Click payment initiation error:', err)
      return false
    } finally {
      setIsPaymentProcessing(false)
    }
  }

  const handlePay = async () => {
    if (isPaymentProcessing || paymentSuccess) return
    const adsId = editAdvertisementId
    if (!adsId) {
      toasterrormsg(t('Advertisement ID is missing.'))
      return
    }
    setIsPaymentProcessing(true)
    const { duration, durationType } = getDurationParams(selectedWeeks)
    try {
      const res: any = await fetchData('public/addeditadspayment', {
        isPurchase: 1,
        vendorId: Number(vendorId),
        adsId: Number(adsId),
        price: getCalculatedPrice(),
        paymentMethod: 'payme',
        transactionNumber: String(adData?.transactionNumber || Date.now()),
        paymentStatus: 'PAID',
        duration,
        durationType,
      })
      if (String(res?.status) === '200') {
        setPaymentSuccess(true)
        sessionStorage.removeItem('editAdvertisementId')
        // Clear location state so a refresh on the success screen doesn't re-trigger
        setTimeout(() => {
          navigate("/viewbanneradvertisment", { replace: true, state: null })
        }, 2000);
        toastsuccessmsg(res?.message || t('Ad Purchased Successfully!'))
        await fetchAdData(adsId)
      } else {
        toasterrormsg(res?.message || t('Payment failed. Please try again.'))
      }
    } catch (err) {
      toasterrormsg(t('Payment failed. Please try again.'))
      console.error('Payment error:', err)
    } finally {
      setIsPaymentProcessing(false)
    }
  }

  const isCurrentTabValid = () => {
    switch (activeTab) {
      case 0: return Object.values(placementFormErrors).every(err => !err)
      case 1: return true
      case 2: return validateBanner()
      case 3: return validateDetail()
      case 4: return validateBudget()
      default: return false
    }
  }

  // Continue handlers
  const goToNextTab = () => {
    const valid = isCurrentTabValid()
    if (activeTab < 4 && valid) {
      setActiveTab(activeTab + 1)
    } else if (!valid) {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { type: 'error', message: t('Please fix errors in current tab before proceeding') } }))
    }
  }

  // Tab content components
  const renderTabContent = () => {
    const selectedPlacement = placementOptions.find((o) => o.placementName === placementFormData.placement)
    const cropDim = placementCropDim[placementFormData.placement] || { w: 312, h: 152 }
    const ratioLabel = cropDim.h === 152 ? '3:1' : '4:1'
    const basePriceNum = selectedPlacement?.amountPerDay || 0
    const daysCount = selectedWeeks === 0
      ? (customDuration.type === 'DAY' ? customDuration.value
        : customDuration.type === 'WEEK' ? customDuration.value * 7
        : customDuration.value * 30)
      : selectedWeeks === 4 ? 30 : selectedWeeks * 7
    const totalAmount = basePriceNum * daysCount

    switch (activeTab) {
      case 0: // Placement
        return (
          <AddAdvertisementPlacementTab
            adPlacementOptions={placementOptions}
            selectedPlacement={placementFormData.placement}
            placementError={placementFormErrors.placement}
            isLoading={placementOptionsLoading}
            loadError={placementOptionsError}
            onSelectPlacement={(placement) => {
              setPlacementFormData(prev => ({ ...prev, placement: placement.name }))
              setSelectedAdssettingId(placement.id)
              setPlacementFormErrors(prev => ({ ...prev, placement: '' }))
            }}
            onNext={handlePlacementNext}
          />
        )

      case 1: // Targeting
        return (
          <AddAdvertisementTargetingTab
            targetingForm={targetingForm}
            onFieldChange={handleTargetingChange}
            onToggleChange={(name, value) => setTargetingForm(prev => ({ ...prev, [name]: value }))}
            onMultiToggleChange={(name, value) => setTargetingForm(prev => ({ ...prev, [name]: value }))}
            onPrevious={() => changeTab(0)}
            onNext={handleTargetingNext}
          />
        )

      case 2: // Banner
        return (
          <AddAdvertisementBannerTab
            cropDim={cropDim}
            ratioLabel={ratioLabel}
            bannerForm={bannerForm}
            bannerErrors={bannerErrors}
            bannerSourceImage={bannerSourceImage}
            bannerCropZoom={bannerCropZoom}
            bannerCropX={bannerCropX}
            bannerCropY={bannerCropY}
            bannerCropApplied={bannerCropApplied}
            bannerIsNewImage={bannerIsNewImage}
            placement={(selectedPlacement && lang !== 'en' && (selectedPlacement as any)[`${lang}_placementName`]) || placementFormData.placement}
            cropContainerRef={bannerContainerRef}
            onFileChange={handleBannerFileChange}
            onCropMouseDown={handleCropMouseDown}
            onCropMouseMove={handleCropMouseMove}
            onCropMouseUp={handleCropMouseUp}
            onCropZoomChange={(value) => {
              setBannerCropZoom(value)
              setBannerCropApplied(false)
            }}
            onApplyCrop={applyBannerCrop}
            onResetCrop={() => {
              setBannerCropZoom(1)
              setBannerCropX(0)
              setBannerCropY(0)
              setBannerCropApplied(false)
              setBannerForm(prev => ({ ...prev, image: null }))
            }}
            onTitleChange={(value) => {
              if (value.length > 40) return
              setBannerForm(prev => ({ ...prev, title: value }))
              setBannerErrors(prev => ({ ...prev, title: '' }))
            }}
            onSubtitleChange={(value) => {
              if (value.length > 60) return
              setBannerForm(prev => ({ ...prev, subtitle: value }))
              setBannerErrors(prev => ({ ...prev, subtitle: '' }))
            }}
            onTitleRuChange={(value) => {
              if (value.length > 40) return
              setBannerForm(prev => ({ ...prev, titleRu: value }))
              setBannerErrors(prev => ({ ...prev, titleRu: '' }))
            }}
            onSubtitleRuChange={(value) => {
              if (value.length > 60) return
              setBannerForm(prev => ({ ...prev, subtitleRu: value }))
            }}
            onTitleUzChange={(value) => {
              if (value.length > 40) return
              setBannerForm(prev => ({ ...prev, titleUz: value }))
              setBannerErrors(prev => ({ ...prev, titleUz: '' }))
            }}
            onSubtitleUzChange={(value) => {
              if (value.length > 60) return
              setBannerForm(prev => ({ ...prev, subtitleUz: value }))
            }}
            onPrevious={() => changeTab(1)}
            onNext={handleBannerNext}
          />
        )

      case 3: // Detail
        return (
          <AddAdvertisementDetailTab
            detailForm={detailForm}
            detailErrors={detailErrors}
            detailEditorRef={detailEditorRef}
            promotionSelection={promotionSelection}
            defaultChildIds={adActionTypeId}
            existingMediaImages={existingMediaImages}
          imageBaseUrl={imageBaseUrl}
            onPromotionChange={setPromotionSelection}
            onInputChange={handleDetailInputChange}
            onDescriptionBlur={handleDetailEditorBlur('mainDescription')}
            onDescriptionChange={handleDetailEditorChange('mainDescription')}
            onDescriptionRuBlur={handleDetailEditorBlur('descriptionRu')}
            onDescriptionRuChange={handleDetailEditorChange('descriptionRu')}
            onDescriptionUzBlur={handleDetailEditorBlur('descriptionUz')}
            onDescriptionUzChange={handleDetailEditorChange('descriptionUz')}
            onFileChange={handleDetailFileChange}
            onRemoveImage={removeDetailImage}
            onRemoveExistingImage={async (adsmediaId) => {
              try {
                const res = await fetchData('public/deleteadsmedia', {
                  vendorId: String(vendorId),
                  adsmediaId: String(adsmediaId),
                })
                if (res?.status === 200 || res?.success) {
                  setExistingMediaImages(prev => prev.filter(m => m.adsmediaId !== adsmediaId))
                  toastsuccessmsg(res?.message || t('Image deleted successfully'))
                } else {
                  toasterrormsg(res?.message || t('Failed to delete image'))
                }
              } catch {
                toasterrormsg(t('Failed to delete image'))
              }
            }}
            onPrevious={() => changeTab(2)}
            onNext={handleDetailNext}
          />
        )

      case 4: { // Budget & Payment
        return (
          <AddAdvertisementBudgetTab
            paymentSuccess={paymentSuccess}
            selectedPlacement={selectedPlacement}
            isFlat={true}
            selectedWeeks={selectedWeeks}
            customDuration={customDuration}
            totalAmount={totalAmount}
            isPriceLoading={false}
            isPaymentProcessing={isPaymentProcessing}
            transactionParam={String(editAdvertisementId ?? '')}
            onSelectWeeks={setSelectedWeeks}
            onSelectCustomDuration={(value, type) => {
              setCustomDuration({ value, type })
              setSelectedWeeks(0)
            }}
            onPay={handlePay}
            onClickPay={handleClickPay}
            onPrevious={() => changeTab(3)}
          />
        )
      }

      default:
        return null
    }
  }

  return (
    <>
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>
        {editAdvertisementId ? t('Edit Advertisement') : t('Add Advertisement')}
      </PageTitle>
      <AddAdvertisementHeader
        advertisementId={editAdvertisementId}
        activeTab={activeTab}
        onTabChange={changeTab}
        completeSteps={adData?.completeSteps ?? null}
      />
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid'>
          <div className='form d-flex flex-column flex-lg-row'>
            <div className='d-flex flex-column flex-row-fluid gap-7 gap-lg-10'>
              <div className='tab-content'>
                <div
                  className={`tab-pane fade show active`}
                  id={`kt_advertisement_tab_${activeTab}`}
                  role='tab-panel'
                >
                  <div className='d-flex flex-column'>
                    {renderTabContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AddAdvertisement
